import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Loader2, AlertCircle, X, PackagePlus, Check
} from 'lucide-react';
import axios from 'axios';
import { ProductAutocomplete } from './forms/ProductAutocomplete';
import { AssetIdEntryModal, assetIdsForQuantity, assetIdsPayload } from './forms/AssetIdEntryModal';
import { WarrantyDropdown } from './forms/WarrantyDropdown';
import { recalculateLineItem } from './lineItemCalculations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Lakshadweep', 'Puducherry'
];

const LINE_ITEM_GRID = '0.5fr 1fr 1fr 1fr 0.7fr 0.4fr 0.8fr 1fr 0.7fr 0.8fr 1fr 0.8fr 0.8fr 1fr';

const emptyLineItem = () => ({
  key: Date.now() + Math.random(),
  product_id: '',
  serial_number: '',
  description: '',
  mrp: '',
  quantity: 1,
  unit_id: '',
  price_per_unit: '',
  discount_percentage: 0,
  discount_amount: 0,
  tax_id: '',
  tax_amount: 0,
  total_amount: 0,
  warranty_id: '',
  warranty_name: '',
  _asset_ids: []
});

export const CreditNoteCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromInvoice = location.state?.fromInvoice || null;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [units, setUnits] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [warranties, setWarranties] = useState([]);

  const [customerId, setCustomerId] = useState(fromInvoice?.customer_id || '');
  const [companyId, setCompanyId] = useState(fromInvoice?.company_id || '');
  const [returnNumber, setReturnNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState(fromInvoice?.invoice_number || '');
  const [invoiceDate, setInvoiceDate] = useState(fromInvoice?.invoice_date || '');
  const [date, setDate] = useState(fromInvoice?.date || new Date().toISOString().split('T')[0]);
  const [stateOfSupply, setStateOfSupply] = useState(fromInvoice?.state_of_supply || '');
  const [sourceInvoiceId] = useState(fromInvoice?.source_invoice_id || null);
  const [sourceInvoiceNumber] = useState(fromInvoice?.source_invoice_number || '');

  const [lineItems, setLineItems] = useState(() =>
    fromInvoice?.line_items?.length
      ? fromInvoice.line_items.map(li => ({
          key: Date.now() + Math.random(),
          product_id: li.product_id || '',
          serial_number: '',
          description: li.description || '',
          mrp: li.mrp || '',
          quantity: li.quantity || 1,
          unit_id: li.unit_id || '',
          price_per_unit: li.price_per_unit || '',
          discount_percentage: li.discount_percentage || 0,
          discount_amount: li.discount_amount || 0,
          tax_id: li.tax_id || '',
          tax_amount: li.tax_amount || 0,
          total_amount: li.total_amount || 0,
          warranty_id: li.warranty_id || '',
          warranty_name: li.warranty_name || '',
          _asset_ids: assetIdsForQuantity(li.asset_ids, li.quantity)
        }))
      : [emptyLineItem()]
  );
  const [assetModalIdx, setAssetModalIdx] = useState(null);
  const [payments, setPayments] = useState([{ key: 1, payment_type: 'Cash', amount: 0 }]);
  const [description, setDescription] = useState(fromInvoice?.description || '');
  const [roundOff, setRoundOff] = useState(fromInvoice?.round_off !== undefined ? fromInvoice.round_off : true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [customersRes, companiesRes, productsRes, taxesRes, unitsRes, warrantiesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/customers`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/taxes`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/units`, getAuthHeaders()),
          axios.get(`${BACKEND_URL}/api/warranties`, getAuthHeaders())
        ]);
        setCustomers(customersRes.data.filter(c => c.active));
        setCompanies(companiesRes.data.filter(c => c.active));
        setProducts(productsRes.data);
        setTaxes(taxesRes.data.filter(t => t.active));
        setUnits(unitsRes.data.filter(u => u.active));
        setWarranties(warrantiesRes.data || []);
        const pmRes = await axios.get(`${BACKEND_URL}/api/payment-modes`, getAuthHeaders());
        setPaymentModes(pmRes.data.filter(p => p.active));
      } catch (err) { setError('Failed to load master data'); }
    };
    fetchMasterData();
  }, []);

  // Pre-fill from bulk DC return (sessionStorage)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('from_dc')) return;
    try {
      const raw = sessionStorage.getItem('dc_return_prefill');
      if (!raw) return;
      const prefill = JSON.parse(raw);
      sessionStorage.removeItem('dc_return_prefill');
      if (prefill.customer_id) setCustomerId(prefill.customer_id);
      if (prefill.company_id) setCompanyId(prefill.company_id);
      if (prefill.state_of_supply) setStateOfSupply(prefill.state_of_supply);
      if (prefill.description) setDescription(prefill.description);
      if (prefill.line_items?.length) {
        const mapped = prefill.line_items.map(li => ({
          ...emptyLineItem(),
          product_id: li.product_id || '',
          description: li.description || '',
          mrp: li.mrp || '',
          quantity: li.quantity || 1,
          unit_id: li.unit_id || '',
          price_per_unit: li.price_per_unit || '',
          discount_percentage: li.discount_percentage || 0,
          discount_amount: li.discount_amount || 0,
          tax_id: li.tax_id || '',
          tax_amount: li.tax_amount || 0,
          total_amount: li.total_amount || 0,
          warranty_id: li.warranty_id || '',
          warranty_name: li.warranty_name || '',
          _asset_ids: assetIdsForQuantity(li.asset_ids, li.quantity),
        }));
        mapped.push(emptyLineItem());
        setLineItems(mapped);
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!companyId) return;
    axios.get(`${BACKEND_URL}/api/invoice-config/${companyId}/next-doc-number/cn`, getAuthHeaders())
      .then(res => { if (res.data.doc_number) setReturnNumber(res.data.doc_number); }).catch(() => {});
  }, [companyId]);

  const recalcLine = (item, changedField = null) => recalculateLineItem(item, taxes, changedField);

  const updateLineItem = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'product_id') {
        updated[index]._asset_ids = [];
        const prod = products.find(p => p.id === value);
        if (prod && prod.unit_id) updated[index].unit_id = prod.unit_id;
        if (value && !updated[updated.length - 1]?.product_id) return updated.map(i => recalcLine(i));
        if (value) updated.push(emptyLineItem());
      }
      if (field === 'quantity') updated[index]._asset_ids = assetIdsForQuantity(updated[index]._asset_ids, value);
      updated[index] = recalcLine(updated[index], field);
      return updated;
    });
  };

  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem()]);
  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };
  const confirmAssetIds = (assetIds) => {
    setLineItems(prev => prev.map((item, index) => index === assetModalIdx ? { ...item, _asset_ids: assetIds } : item));
    setAssetModalIdx(null);
  };

  const addPayment = () => setPayments(prev => [...prev, { key: Date.now(), payment_type: 'Cash', amount: 0 }]);
  const updatePayment = (index, field, value) => {
    setPayments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  const removePayment = (index) => {
    if (payments.length <= 1) return;
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  const subtotal = lineItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);
  const roundOffAmount = roundOff ? Math.round(subtotal) - subtotal : 0;
  const total = roundOff ? Math.round(subtotal) : Math.round(subtotal * 100) / 100;
  const totalQty = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const totalDiscount = lineItems.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const totalTax = lineItems.reduce((sum, item) => sum + (item.tax_amount || 0), 0);

  const handleSave = async () => {
    setError('');
    if (!customerId) { setError('Please select a customer (party)'); return; }
    if (!lineItems.some(li => li.product_id)) { setError('Please add at least one product'); return; }

    setSubmitting(true);
    try {
      const payload = {
        customer_id: customerId,
        company_id: companyId || null,
        return_number: returnNumber,
        invoice_number: invoiceNumber || null,
        invoice_date: invoiceDate || null,
        date: date,
        state_of_supply: stateOfSupply,
        source_invoice_id: sourceInvoiceId || null,
        line_items: lineItems.filter(li => li.product_id).map(li => ({
          product_id: li.product_id,
          description: li.description,
          mrp: parseFloat(li.mrp) || null,
          quantity: parseFloat(li.quantity) || 0,
          unit_id: li.unit_id || null,
          price_per_unit: parseFloat(li.price_per_unit) || 0,
          discount_percentage: parseFloat(li.discount_percentage) || 0,
          discount_amount: li.discount_amount,
          tax_id: li.tax_id || null,
          tax_amount: li.tax_amount,
          total_amount: li.total_amount,
          warranty_id: li.warranty_id || null,
          warranty_name: li.warranty_name || null,
          asset_ids: assetIdsPayload(li._asset_ids, li.quantity)
        })),
        payments: payments.map(p => ({
          payment_type: p.payment_type,
          amount: parseFloat(p.amount) || 0,
          bank_detail_id: p.bank_detail_id || null
        })),
        description: description || null,
        round_off: roundOff,
        round_off_amount: Math.round(roundOffAmount * 100) / 100,
        subtotal: Math.round(subtotal * 100) / 100,
        total
      };
      await axios.post(`${BACKEND_URL}/api/credit-notes`, payload, getAuthHeaders());
      navigate('/admin/sale/return');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save credit note');
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="credit-note-create-page" className="pb-24">
      <button onClick={() => navigate('/admin/sale/return')} data-testid="back-btn"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Credit Notes
      </button>

      <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight mb-8">Credit Note</h1>

      {sourceInvoiceId && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-sm mb-6" data-testid="from-invoice-banner">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">
            Sale Return against Invoice{sourceInvoiceNumber ? <strong className="ml-1">{sourceInvoiceNumber}</strong> : ''}. Review the pre-filled data and click Save to issue the Credit Note.
          </span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* HEADER */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Party (Customer) <span className="text-red-500">*</span></label>
            <ProductAutocomplete value={customerId} onChange={(id) => setCustomerId(id)} products={customers} testId="select-customer" placeholder="Type customer name..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="select-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select Company</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Return No.</label>
            <input type="text" value={returnNumber} onChange={(e) => setReturnNumber(e.target.value)} placeholder="e.g., CN/25-26/1"
              data-testid="input-return-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Number</label>
            <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="Original invoice number"
              data-testid="input-invoice-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Invoice Date</label>
            <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}
              data-testid="input-invoice-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              data-testid="input-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State of Supply</label>
            <select value={stateOfSupply} onChange={(e) => setStateOfSupply(e.target.value)} data-testid="select-state"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* LINE ITEMS */}
      <div className="bg-white rounded-sm border border-slate-200 mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Line Items</h2>
          <button onClick={addLineItem} data-testid="add-line-item-btn"
            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-sm">
            <Plus className="h-4 w-4" /> Add Row
          </button>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[1400px]">
            <div className="grid gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider"
              style={{ gridTemplateColumns: LINE_ITEM_GRID }}>
              <div className="col-span-1">#</div>
              <div className="col-span-2">Product</div>
              <div className="col-span-1">MRP</div>
              <div className="col-span-1">Qty</div>
              <div className="col-span-1"></div>
              <div className="col-span-1">Unit</div>
              <div className="col-span-1">Price/Unit</div>
              <div className="col-span-1">Disc %</div>
              <div className="col-span-1">Disc Amt</div>
              <div className="col-span-1">Tax</div>
              <div className="col-span-1">Tax Amt</div>
              <div className="col-span-1">Warranty</div>
              <div className="col-span-1">Amount</div>
            </div>
            {lineItems.map((item, idx) => (
              <div key={item.key} data-testid={`line-item-${idx}`}
                className="grid gap-2 px-4 py-2 border-b border-slate-100 items-center"
                style={{ gridTemplateColumns: LINE_ITEM_GRID }}>
                <div className="col-span-1 flex items-center gap-1">
                  <span className="text-slate-500 text-sm">{idx + 1}</span>
                  {lineItems.length > 1 && (
                    <button onClick={() => removeLineItem(idx)} data-testid={`remove-line-${idx}`}
                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"><Trash2 className="h-3 w-3" /></button>
                  )}
                </div>
                <div className="col-span-2">
                  <ProductAutocomplete
                    value={item.product_id}
                    onChange={(pid) => updateLineItem(idx, 'product_id', pid)}
                    products={products}
                    testId={`line-product-${idx}`}
                    placeholder="Type product name..."
                  />
                  <input type="text" value={item.serial_number} onChange={(e) => updateLineItem(idx, 'serial_number', e.target.value)}
                    placeholder="Serial Number" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                  <input type="text" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    placeholder="Description" className="w-full px-2 py-1 border border-slate-100 rounded-sm text-xs mt-1 focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.mrp} onChange={(e) => updateLineItem(idx, 'mrp', e.target.value)}
                    placeholder="0" className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                    min="1" step="1" data-testid={`line-qty-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1 pt-1">
                  {item.product_id && (parseInt(item.quantity) || 0) > 0 && (() => {
                    const hasAssetIds = (item._asset_ids || []).some(assetId => assetId?.trim());
                    return (
                      <button type="button" onClick={() => setAssetModalIdx(idx)} data-testid={`line-asset-btn-${idx}`}
                        className={`p-1.5 rounded transition-colors ${hasAssetIds ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-orange-500 bg-orange-50 hover:bg-orange-100'}`}
                        title={hasAssetIds ? `${item._asset_ids.filter(assetId => assetId?.trim()).length} asset IDs entered` : 'Enter Asset IDs'}>
                        {hasAssetIds ? <Check className="h-4 w-4" /> : <PackagePlus className="h-4 w-4" />}
                      </button>
                    );
                  })()}
                </div>
                <div className="col-span-1">
                  <select value={item.unit_id} onChange={(e) => updateLineItem(idx, 'unit_id', e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.price_per_unit} onChange={(e) => updateLineItem(idx, 'price_per_unit', e.target.value)}
                    placeholder="0" step="0.01" data-testid={`line-price-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1">
                  <input type="number" value={item.discount_percentage} onChange={(e) => updateLineItem(idx, 'discount_percentage', e.target.value)}
                    min="0" max="100" step="0.01"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                </div>
                <div className="col-span-1"><input type="number" value={item.discount_amount} onChange={(e) => updateLineItem(idx, 'discount_amount', e.target.value)} min="0" step="0.01" data-testid={`line-discount-amount-${idx}`} className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" /></div>
                <div className="col-span-1">
                  <select value={item.tax_id} onChange={(e) => updateLineItem(idx, 'tax_id', e.target.value)}
                    data-testid={`line-tax-${idx}`}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none">
                    <option value="">-</option>
                    {taxes.map(t => <option key={t.id} value={t.id}>{t.name} ({t.percentage}%)</option>)}
                  </select>
                </div>
                <div className="col-span-1"><span className="text-sm text-slate-600 px-2">{item.tax_amount?.toFixed(2)}</span></div>
                <div className="col-span-1">
                  <WarrantyDropdown
                    value={item.warranty_id}
                    onChange={(wid, wname) => {
                      updateLineItem(idx, 'warranty_id', wid);
                      updateLineItem(idx, 'warranty_name', wname || '');
                    }}
                    warranties={warranties}
                    testId={`line-warranty-${idx}`}
                  />
                </div>
                <div className="col-span-1"><span className="text-sm font-medium text-slate-900 px-2">{item.total_amount?.toFixed(2)}</span></div>
              </div>
            ))}
            <div className="grid gap-2 px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm font-semibold"
              style={{ gridTemplateColumns: LINE_ITEM_GRID }}>
              <div className="col-span-1"></div>
              <div className="col-span-2 text-slate-700">TOTAL</div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-slate-700">{totalQty}</div>
              <div className="col-span-1"></div>
              <div className="col-span-1"></div>
              <div className="col-span-1"></div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-slate-700">{totalDiscount.toFixed(2)}</div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-slate-700">{totalTax.toFixed(2)}</div>
              <div className="col-span-1"></div>
              <div className="col-span-1 text-slate-900">{subtotal.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM — Payment + Description + Round Off + Total */}
      <div className="bg-white rounded-sm border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            {payments.map((p, idx) => (
              <div key={p.key} className="flex items-center gap-3">
                <div className="flex-1 space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Payment Mode</label>
                    <select value={p.payment_type} onChange={(e) => updatePayment(idx, 'payment_type', e.target.value)}
                      data-testid={`payment-type-${idx}`}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                      <option value="">Select</option>
                      {paymentModes.map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                    </select>
                  </div>
                  {(p.payment_type === 'Online' || p.payment_type === 'UPI') && (() => {
                    const selectedCompany = companies.find(c => c.id === companyId);
                    if (!companyId || !selectedCompany) return (
                      <p className="text-xs text-amber-600">Please select a company first to see bank accounts</p>
                    );
                    const filterType = p.payment_type === 'Online' ? 'Net Banking' : 'UPI';
                    const filtered = (selectedCompany.bank_details || []).filter(bd => bd.type === filterType);
                    if (filtered.length === 0) return (
                      <p className="text-xs text-slate-400">No {filterType} accounts added for {selectedCompany.company_name}</p>
                    );
                    return (
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {p.payment_type === 'Online' ? 'Bank Account' : 'UPI ID'}
                        </label>
                        <select value={p.bank_detail_id || ''} onChange={(e) => updatePayment(idx, 'bank_detail_id', e.target.value)}
                          data-testid={`bank-detail-${idx}`}
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm">
                          <option value="">-- Select --</option>
                          {filtered.map(bd => (
                            <option key={bd.id} value={bd.id}>
                              {bd.type === 'Net Banking' ? `${bd.bank_name || ''} - ${bd.account_no || ''}` : bd.upi_id || bd.bank_name || ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>
                {payments.length > 1 && (
                  <button onClick={() => removePayment(idx)} className="mt-5 p-2 text-slate-400 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Add Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} placeholder="Add a note or description..."
                data-testid="input-description"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none text-sm resize-none" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)}
                  data-testid="round-off-checkbox" className="w-4 h-4 text-orange-500 border-slate-300 rounded" />
                <span className="text-sm text-slate-600">Round Off</span>
                {roundOff && <span className="text-sm text-slate-500">{roundOffAmount >= 0 ? '+' : ''}{roundOffAmount.toFixed(2)}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-700">Total</span>
                <span className="text-lg font-bold text-slate-900 min-w-[100px] text-right" data-testid="total-amount">
                  ₹{total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AssetIdEntryModal
        open={assetModalIdx !== null}
        productName={products.find(product => product.id === lineItems[assetModalIdx]?.product_id)?.name}
        quantity={lineItems[assetModalIdx]?.quantity}
        initialValues={lineItems[assetModalIdx]?._asset_ids}
        onClose={() => setAssetModalIdx(null)}
        onConfirm={confirmAssetIds}
      />

      {/* SAVE */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end z-40">
        <button onClick={handleSave} disabled={submitting} data-testid="save-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-8 py-2.5 rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
      </div>
    </div>
  );
};
